import { FormInstance } from 'antd';
import { SeriesColumnOptions } from 'highcharts';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  AggregateType,
  Field,
  UnitPosition,
  WidgetDefaultNumberFields,
  WidgetType,
} from 'api/dashboard/widget';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { Switch } from 'components/atoms/switch/switch';
import { GroupedFilterDrawerWithTrigger } from 'components/molecules/filter-drawer';
import { GroupSelect } from 'components/molecules/group-select';
import { getNumberWidgetContent } from 'components/organisms/dashboard/components/number-widget';
import { NumberWidgetCard } from 'components/organisms/dashboard/components/number-widget-card';
import {
  FieldType,
  FilterableField,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { ApiFilterTransformer } from 'components/pages/api-filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useTranslate } from 'hooks/use-translation';
import { usePreviewWidgetQuery } from 'queries/widgets';
import {
  BodyShortText,
  CaptionText,
  CenterWrapper,
  Content,
  FilterWrapper,
  HelperTextWrapper,
  OptionsCol,
  Row,
  SimpleWrapper,
  Strong,
  StyledCol,
  StyledFormItem,
} from './styles';
import { dateFilterOperationOptions, GroupedFields } from './utils';
import { ErrorState } from './widget-state';

const FormItem = styled(Form.Item)`
  margin-bottom: 0;
`;
const Flex = styled.div`
  display: flex;
`;
const StyledSwitch = styled(Switch)`
  margin-right: var(--spacing-03);
`;
const StyledButton = styled(Button)`
  padding: 0;
  left: 0;
`;
const NumberWidgetWrapper = styled.div`
  width: 20rem;
`;

export type CustomCardFormValues = {
  widgetName: string;
  unitName?: string;
  aggType: AggregateType;
  field: {
    label: string;
    value: Field;
  };
  prefix: boolean;
};

interface CustomCardProps {
  form: FormInstance<CustomCardFormValues>;
  filterFields: GroupedFilter;
  groupedFields: GroupedOptions<FilterableField>[];
  transformer: ApiFilterTransformer<GroupedFilter>;
  numberFieldOptions: GroupedFields[];
  setFilter: React.Dispatch<React.SetStateAction<GroupedFilter>>;
  initialValues: CustomCardFormValues;
  onFinish: (values: CustomCardFormValues) => void;
  defaultGroup: FilterGroupType;
}

const aggregateRules = (value: string, formFields) => {
  switch (value) {
    case WidgetDefaultNumberFields.CSAT:
      formFields.setFieldsValue({
        aggType: AggregateType.Average,
      });

      return {
        aggType: AggregateType.Average,
        disableTotal: true,
        disableAvg: false,
      };
    case WidgetDefaultNumberFields.CustomerCount:
    case WidgetDefaultNumberFields.ProjectCount:
      formFields.setFieldsValue({
        aggType: AggregateType.Total,
      });

      return {
        aggType: AggregateType.Total,
        disableTotal: false,
        disableAvg: true,
      };
    default:
      return {
        disableTotal: false,
        disableAvg: false,
      };
  }
};

export const CustomCards: FC<CustomCardProps> = ({
  form,
  setFilter,
  groupedFields,
  transformer,
  filterFields,
  initialValues,
  onFinish,
  numberFieldOptions,
  defaultGroup,
}) => {
  const [showCurrencyinput, setShowCurrencyInput] = useState(
    !!initialValues.unitName,
  );
  const [cardFields, setCardFields] = useState(() => {
    let rules;

    if ('name' in initialValues.field.value) {
      rules = aggregateRules(initialValues.field.value.name, form);
    }

    return {
      ...initialValues,
      ...rules,
      data: 0,
    };
  });

  const translate = useTranslate();

  const { data: previewData, status } = usePreviewWidgetQuery({
    simpleSearchFilter: transformer.to(filterFields),
    widgetType: WidgetType.CustomCard,
    metaData: {
      field: cardFields.field.value,
      aggType: cardFields.aggType,
    },
  });

  const content = useMemo(() => {
    const value = Number(
      (previewData?.series?.[0] as SeriesColumnOptions)?.data?.[0] ?? 0,
    );

    switch (status) {
      case 'loading':
        return <Loading />;
      case 'error':
        return (
          <ErrorState
            size="small"
            direction="horizontal"
            message={<FormattedMessage id="dashboard.widget.number.error" />}
          />
        );
      case 'success':
        return previewData ? (
          <Content>
            {getNumberWidgetContent(
              WidgetType.CustomCard,
              value,
              cardFields.unitName,
              cardFields.prefix ? UnitPosition.Prefix : UnitPosition.Suffix,
              cardFields.field.value,
            )}

            <HelperTextWrapper>
              {cardFields.aggType === AggregateType.Total
                ? translate('dashboard.custom_charts.label.total')
                : translate('dashboard.custom_charts.label.avg')}
            </HelperTextWrapper>
          </Content>
        ) : null;

      case 'idle':
      default:
        return null;
    }
  }, [cardFields, previewData, status, translate]);

  return (
    <Row>
      <StyledCol>
        <CenterWrapper>
          <NumberWidgetWrapper>
            <NumberWidgetCard
              filter={filterFields}
              widgetName={
                cardFields.widgetName || (
                  <FormattedMessage id="dashboard.custom_card.cardName.default.state" />
                )
              }
              preview
              content={content}
            />
          </NumberWidgetWrapper>
        </CenterWrapper>
      </StyledCol>
      <OptionsCol>
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={(values) => onFinish(values as CustomCardFormValues)}
        >
          <Form.Item
            label={<FormattedMessage id="dashboard.custom_card.card_name" />}
            name="widgetName"
            rules={[
              {
                whitespace: true,
                required: true,
                message: translate(
                  'dashbaord.custom_card.name.input.error,message',
                ),
              },
            ]}
          >
            <Input
              onChange={(e) => {
                const { value } = e.target;

                setCardFields((fields) => {
                  return {
                    ...fields,
                    widgetName: value,
                  };
                });
              }}
              placeholder={translate('dashboard.custom_card.card_name')}
            />
          </Form.Item>
          <StyledFormItem
            label={<FormattedMessage id="dashboard.custom_card.field_name" />}
            name="field"
          >
            <GroupSelect
              options={numberFieldOptions}
              onChange={(e) => {
                const rule = aggregateRules(e.value.name, form);

                setCardFields((fields) => {
                  return {
                    ...fields,
                    ...rule,
                    field: e,
                  };
                });
              }}
            />
          </StyledFormItem>

          <FormItem name="aggType">
            <Radio.Group
              value={cardFields.aggType}
              onChange={(e) => {
                setCardFields((fields) => {
                  return {
                    ...fields,
                    aggType: e.target.value,
                  };
                });
              }}
            >
              <Radio
                value={AggregateType.Total}
                disabled={cardFields.disableTotal}
              >
                <FormattedMessage id="dashboard.custom_charts.label.total" />
              </Radio>
              <Radio
                value={AggregateType.Average}
                disabled={cardFields.disableAvg}
              >
                <FormattedMessage id="dashboard.custom_charts.label.average" />
              </Radio>
            </Radio.Group>
          </FormItem>

          <StyledFormItem>
            {!showCurrencyinput ? (
              <>
                <Form.Item>
                  <StyledButton
                    onClick={() => {
                      setShowCurrencyInput(true);
                    }}
                    type="link"
                  >
                    <FormattedMessage id="dashboard.custom_card.add_unit" />
                  </StyledButton>
                  <CaptionText>
                    <FormattedMessage id="dashboard.custom_card.unit" />
                  </CaptionText>
                </Form.Item>
              </>
            ) : (
              <>
                <StyledFormItem
                  label="Unit"
                  name="unitName"
                  rules={[
                    {
                      whitespace: true,
                    },
                    {
                      max: 3,
                      message: translate(
                        'dashboard.custom_card.card_name.unit.max_length.message',
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    placeholder={translate(
                      'dashboard.custom_card.card_name.unit.placeholder',
                    )}
                    value={cardFields.unitName}
                    onChange={(e) => {
                      const { value } = e.target;

                      setCardFields((fields) => {
                        return {
                          ...fields,
                          unitName: value,
                        };
                      });
                    }}
                  />
                </StyledFormItem>
                <Flex>
                  <BodyShortText>
                    <FormattedMessage id="dashboard.custom_cards.add_prefix" />
                  </BodyShortText>
                  <Form.Item name="prefix">
                    <StyledSwitch
                      size="small"
                      checked={cardFields.prefix}
                      onChange={() =>
                        setCardFields((fields) => {
                          return {
                            ...fields,
                            prefix: !fields.prefix,
                          };
                        })
                      }
                    />
                  </Form.Item>
                </Flex>
              </>
            )}
          </StyledFormItem>
          <Form.Item>
            <FilterWrapper>
              <Strong>
                <FormattedMessage id="dashboard.custom_charts.filter" />
              </Strong>
            </FilterWrapper>
            <SimpleWrapper>
              <GroupedFilterDrawerWithTrigger
                defaultGroup={defaultGroup}
                fields={groupedFields}
                filter={filterFields}
                onSubmit={setFilter}
                getFilterOperationTypeOptions={{
                  [FieldType.Date]: dateFilterOperationOptions,
                }}
              />
            </SimpleWrapper>
          </Form.Item>
        </Form>
      </OptionsCol>
    </Row>
  );
};
